import { useCallback, useEffect, useState } from "react";
import { AlertCircle, CalendarDays, Check, Download, FileSpreadsheet, LocateFixed, MapPin, Plus, Printer, RefreshCw, Save, ShieldCheck, UserRoundCheck, UsersRound, X } from "lucide-react";
import { api, ApiError } from "../lib/api";
import type { Screen } from "../types";

type EmployeeItem = {
  id: string;
  employee_code: string;
  full_name: string;
  position: string;
  role: string;
  active: number;
};

type Workplace = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  radius_meters: number;
  standard_start_time: string;
  standard_end_time: string;
  break_minutes: number;
  late_grace_minutes: number;
  ot_threshold_minutes: number;
};
type Notice = { tone: "success" | "error"; text: string } | null;

export function ManagerSurface({ screen }: { screen: Screen }) {
  if (screen === "employees") return <Employees />;
  if (screen === "settings") return <Settings />;
  if (screen === "reports") return <Reports />;
  if (screen === "leave") return <Leave />;
  return <Overview />;
}

function Overview() {
  const [summary, setSummary] = useState({ working: 0, scheduled: 0, attention: 0, pendingLeave: 0, absent: 0 });
  const [employees, setEmployees] = useState(0);
  useEffect(() => {
    Promise.all([
      api<typeof summary>("/api/dashboard"),
      api<{ items: EmployeeItem[] }>("/api/manager/employees")
    ]).then(([dashboard, people]) => { setSummary(dashboard); setEmployees(people.items.filter((item) => item.active).length); }).catch(() => undefined);
  }, []);
  return (
    <main className="manager-main" id="main-content">
      <PageHeading title="ภาพรวมวันนี้" description="ข้อมูลเวลาจริงจากพนักงาน คำนวณตามเวลามาตรฐานของร้าน" />
      {employees === 0 && <div className="setup-banner"><span><UsersRound size={23} /></span><div><strong>เริ่มจากเพิ่มพนักงาน</strong><p>ตั้งพิกัดและเวลาทำงานมาตรฐานในเมนูตั้งค่า แล้วเพิ่มบัญชีพนักงานก่อนส่งรหัสให้ทีม</p></div></div>}
      <section className="live-summary" aria-label="สถานะร้านวันนี้">
        <SummaryItem label="พนักงานทั้งหมด" value={employees} icon={UsersRound} />
        <SummaryItem label="พร้อมลงเวลา" value={summary.scheduled} icon={CalendarDays} />
        <SummaryItem label="เข้างานแล้ว" value={summary.working} icon={UserRoundCheck} />
        <SummaryItem label="ผิดปกติ" value={summary.attention} icon={AlertCircle} />
      </section>
      <section className="manager-empty-workbench">
        <ShieldCheck size={30} />
        <h2>เวลาเข้าระบบอัตโนมัติ</h2>
        <p>เมื่อพนักงานผ่านรูปถ่ายและ GPS ระบบจะคำนวณมาสาย เวลาทำงาน และ OT จากเวลามาตรฐานโดยอัตโนมัติ</p>
      </section>
    </main>
  );
}

function SummaryItem({ label, value, icon: Icon }: { label: string; value: number; icon: typeof UsersRound }) {
  return <div><span><Icon size={19} /></span><strong>{value}</strong><small>{label}</small></div>;
}

function Employees() {
  const [items, setItems] = useState<EmployeeItem[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [position, setPosition] = useState("");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const load = useCallback(() => api<{ items: EmployeeItem[] }>("/api/manager/employees").then((result) => setItems(result.items)), []);
  useEffect(() => { void load(); }, [load]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const result = await api<{ employee: { code: string }; temporaryPin: string }>("/api/manager/employees", { method: "POST", body: JSON.stringify({ fullName, position, code, pin }) });
      setNotice({ tone: "success", text: `สร้างบัญชีแล้ว · รหัส ${result.employee.code} · PIN ชั่วคราว ${result.temporaryPin} — กรุณาจดหรือส่งให้พนักงานตอนนี้` });
      setFullName(""); setPosition(""); setCode(""); setPin(""); setShowForm(false);
      await load();
    } catch (error) {
      setNotice({ tone: "error", text: errorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  const resetPin = async (item: EmployeeItem) => {
    if (!window.confirm(`ออก PIN ใหม่ให้ ${item.full_name} ใช่หรือไม่?`)) return;
    try {
      const result = await api<{ temporaryPin: string }>(`/api/manager/employees/${item.id}`, { method: "PATCH", body: JSON.stringify({ resetPin: true }) });
      setNotice({ tone: "success", text: `PIN ชั่วคราวใหม่ของ ${item.full_name}: ${result.temporaryPin} — PIN เดิมใช้ไม่ได้แล้ว` });
    } catch (error) {
      setNotice({ tone: "error", text: errorMessage(error) });
    }
  };

  const setActive = async (item: EmployeeItem, active: boolean) => {
    try {
      await api(`/api/manager/employees/${item.id}`, { method: "PATCH", body: JSON.stringify({ active }) });
      await load();
    } catch (error) {
      setNotice({ tone: "error", text: errorMessage(error) });
    }
  };

  return (
    <main className="manager-main" id="main-content">
      <PageHeading title="พนักงาน" description="เพิ่มบัญชีและออก PIN แยกแต่ละคน" action={<button className="button primary" onClick={() => setShowForm((value) => !value)}>{showForm ? <X size={18} /> : <Plus size={18} />}{showForm ? "ปิดฟอร์ม" : "เพิ่มพนักงาน"}</button>} />
      <NoticeBar notice={notice} onClose={() => setNotice(null)} />
      {showForm && <form className="inline-manager-form" onSubmit={create}>
        <div><h2>พนักงานใหม่</h2><p>ระบบสามารถสร้างรหัสและ PIN ให้โดยอัตโนมัติ</p></div>
        <label>ชื่อ–นามสกุล<input value={fullName} onChange={(event) => setFullName(event.target.value)} placeholder="ชื่อพนักงาน" required /></label>
        <label>ตำแหน่ง<input value={position} onChange={(event) => setPosition(event.target.value)} placeholder="เช่น พนักงานครัว" required /></label>
        <label>รหัสพนักงาน <small>เว้นว่างเพื่อสร้างอัตโนมัติ</small><input value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} placeholder="EMP001" pattern="[A-Z0-9-]{3,20}" /></label>
        <label>PIN 6 หลัก <small>เว้นว่างเพื่อสุ่มปลอดภัย</small><input value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" pattern="[0-9]{6}" placeholder="สุ่มอัตโนมัติ" /></label>
        <button className="button primary" disabled={saving}><Save size={18} />{saving ? "กำลังสร้าง…" : "สร้างบัญชี"}</button>
      </form>}
      <section className="real-data-list" aria-label="รายชื่อพนักงาน">
        <div className="data-list-header"><strong>พนักงาน {items.filter((item) => item.active).length} คน</strong><span>รหัสเข้าสู่ระบบ</span><span>สถานะ</span><span>จัดการ</span></div>
        {items.length === 0 ? <EmptyState icon={UsersRound} title="ยังไม่มีพนักงาน" text="กดเพิ่มพนักงานเพื่อสร้างบัญชีคนแรก" /> : items.map((item) => <article className={`data-list-row ${item.active ? "" : "is-inactive"}`} key={item.id}>
          <span className="employee-avatar">{item.full_name.slice(0, 1)}</span>
          <div className="data-person"><strong>{item.full_name}</strong><small>{item.position}</small></div>
          <code>{item.employee_code}</code>
          <span>{item.active ? "พร้อมใช้งาน" : "พักใช้งาน"}</span>
          <div className="row-actions"><button className="button secondary small" onClick={() => void resetPin(item)}><RefreshCw size={15} /> PIN ใหม่</button><button className="text-button danger-text" onClick={() => void setActive(item, !Boolean(item.active))}>{item.active ? "พักใช้งาน" : "เปิดใช้งาน"}</button></div>
        </article>)}
      </section>
    </main>
  );
}

function Settings() {
  const [workplace, setWorkplace] = useState<Workplace | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api<{ workplace: Workplace }>("/api/manager/workplace").then((result) => setWorkplace(result.workplace)).catch((error) => setNotice({ tone: "error", text: errorMessage(error) })); }, []);

  const useCurrentLocation = () => {
    setNotice(null);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => setWorkplace((current) => current ? { ...current, latitude: Number(coords.latitude.toFixed(6)), longitude: Number(coords.longitude.toFixed(6)) } : current),
      () => setNotice({ tone: "error", text: "หาพิกัดไม่ได้ โปรดเปิด GPS และอนุญาตตำแหน่ง" }),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 }
    );
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!workplace) return;
    setSaving(true); setNotice(null);
    try {
      const result = await api<{ workplace: Workplace }>("/api/manager/workplace", { method: "PATCH", body: JSON.stringify({
        name: workplace.name,
        latitude: workplace.latitude,
        longitude: workplace.longitude,
        radius: workplace.radius_meters,
        standardStartTime: workplace.standard_start_time,
        standardEndTime: workplace.standard_end_time,
        breakMinutes: workplace.break_minutes,
        lateGraceMinutes: workplace.late_grace_minutes,
        otThresholdMinutes: workplace.ot_threshold_minutes
      }) });
      setWorkplace(result.workplace);
      setNotice({ tone: "success", text: "บันทึกพิกัดและเวลาทำงานมาตรฐานแล้ว ระบบจะใช้ค่านี้ทันที" });
    } catch (error) { setNotice({ tone: "error", text: errorMessage(error) }); }
    finally { setSaving(false); }
  };

  return (
    <main className="manager-main settings-page" id="main-content">
      <PageHeading title="ตั้งค่าร้าน" description="กำหนดจุดลงเวลาและเวลาทำงานสำหรับพนักงานทุกคน" />
      <NoticeBar notice={notice} onClose={() => setNotice(null)} />
      {workplace && <form className="settings-stack" onSubmit={save}>
        <section className="settings-section production-settings">
          <div><h2>เวลาทำงานมาตรฐาน</h2><p>ตั้งครั้งเดียวสำหรับทุกคน ใช้คำนวณมาสาย เวลาทำงาน และ OT</p></div>
          <div className="settings-form">
            <div className="field-row"><label>เวลาเริ่มงาน<input type="time" value={workplace.standard_start_time} onChange={(event) => setWorkplace({ ...workplace, standard_start_time: event.target.value })} /></label><label>เวลาเลิกงาน<input type="time" value={workplace.standard_end_time} onChange={(event) => setWorkplace({ ...workplace, standard_end_time: event.target.value })} /></label></div>
            <label>เวลาพัก<div className="input-suffix"><input type="number" min="0" max="240" step="5" value={workplace.break_minutes} onChange={(event) => setWorkplace({ ...workplace, break_minutes: Number(event.target.value) })} /><span>นาที</span></div></label>
            <label>ผ่อนผันก่อนเริ่มนับสาย<div className="input-suffix"><input type="number" min="0" max="120" step="1" value={workplace.late_grace_minutes} onChange={(event) => setWorkplace({ ...workplace, late_grace_minutes: Number(event.target.value) })} /><span>นาที</span></div></label>
            <label>เริ่มนับ OT เมื่อเกินเวลาเลิกงาน<div className="input-suffix"><input type="number" min="0" max="240" step="5" value={workplace.ot_threshold_minutes} onChange={(event) => setWorkplace({ ...workplace, ot_threshold_minutes: Number(event.target.value) })} /><span>นาที</span></div></label>
          </div>
        </section>
        <section className="settings-section">
          <div><h2>จุดลงเวลา</h2><p>ควรตั้งขณะยืนที่ร้าน ระบบจะปฏิเสธทันทีเมื่ออยู่นอกรัศมี</p></div>
          <div className="settings-form">
            <label>ชื่อร้านหรือจุดลงเวลา<input value={workplace.name} onChange={(event) => setWorkplace({ ...workplace, name: event.target.value })} /></label>
            <button className="button secondary location-capture" type="button" onClick={useCurrentLocation}><LocateFixed size={18} /> ใช้พิกัดมือถือเครื่องนี้</button>
            <div className="field-row"><label>ละติจูด<input value={workplace.latitude} inputMode="decimal" onChange={(event) => setWorkplace({ ...workplace, latitude: Number(event.target.value) })} /></label><label>ลองจิจูด<input value={workplace.longitude} inputMode="decimal" onChange={(event) => setWorkplace({ ...workplace, longitude: Number(event.target.value) })} /></label></div>
            <label>รัศมีที่อนุญาต<div className="input-suffix"><input type="number" min="20" max="1000" value={workplace.radius_meters} onChange={(event) => setWorkplace({ ...workplace, radius_meters: Number(event.target.value) })} /><span>เมตร</span></div></label>
            <div className="coordinate-preview"><MapPin size={18} /><span>{workplace.latitude.toFixed(6)}, {workplace.longitude.toFixed(6)}</span></div>
          </div>
        </section>
        <div className="settings-save-row"><button className="button primary" disabled={saving}><Save size={18} />{saving ? "กำลังบันทึก…" : "บันทึกการตั้งค่า"}</button></div>
      </form>}
    </main>
  );
}

function Reports() {
  const monthNow = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok", year: "numeric", month: "2-digit" }).format(new Date());
  const [month, setMonth] = useState(monthNow);
  const [rows, setRows] = useState<Array<{ full_name: string; position: string; work_days: number; work_minutes: number; late_minutes: number; ot_minutes: number; leave_days: number }>>([]);
  useEffect(() => { api<{ items: typeof rows }>(`/api/reports?month=${month}`).then((result) => setRows(result.items)); }, [month]);
  const exportExcel = () => {
    const header = ["พนักงาน", "ตำแหน่ง", "วันทำงาน", "ชั่วโมงทำงาน", "มาสาย (นาที)", "OT (นาที)", "ลา (วัน)"];
    const csv = [header, ...rows.map((row) => [row.full_name, row.position, row.work_days, (row.work_minutes / 60).toFixed(2), row.late_minutes, row.ot_minutes, row.leave_days])].map((line) => line.map(csvCell).join(",")).join("\n");
    downloadBlob(new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }), `รายงานเวลา-${month}.csv`);
  };
  return (
    <main className="manager-main report-page" id="main-content">
      <PageHeading title="รายงานเวลาทำงาน" description="ข้อมูลจริงจากเวลาเข้า–ออกที่ระบบคำนวณ" action={<div className="heading-actions"><button className="button secondary" onClick={() => window.print()}><Printer size={18} /> PDF</button><button className="button primary" onClick={exportExcel}><FileSpreadsheet size={18} /> Excel/CSV</button></div>} />
      <label className="date-control report-month"><span>เดือน</span><input type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label>
      <section className="report-table-wrap"><table className="report-table"><thead><tr><th>พนักงาน</th><th>ตำแหน่ง</th><th>วันทำงาน</th><th>ชั่วโมง</th><th>มาสาย</th><th>OT</th><th>ลา</th></tr></thead><tbody>{rows.map((row) => <tr key={row.full_name}><td data-label="พนักงาน"><strong>{row.full_name}</strong></td><td data-label="ตำแหน่ง">{row.position}</td><td data-label="วันทำงาน">{row.work_days}</td><td data-label="ชั่วโมง">{(row.work_minutes / 60).toFixed(2)}</td><td data-label="มาสาย">{row.late_minutes} นาที</td><td data-label="OT">{row.ot_minutes} นาที</td><td data-label="ลา">{row.leave_days}</td></tr>)}</tbody></table>{rows.length === 0 && <EmptyState icon={Download} title="ยังไม่มีข้อมูลในเดือนนี้" text="ข้อมูลจะปรากฏหลังพนักงานลงเวลา" />}</section>
    </main>
  );
}

function Leave() {
  const [items, setItems] = useState<Array<{ id: string; full_name: string; position: string; leave_type: string; start_date: string; end_date: string; days: number; reason: string; status: string }>>([]);
  const [notice, setNotice] = useState<Notice>(null);
  const load = useCallback(() => api<{ items: typeof items }>("/api/leaves").then((result) => setItems(result.items)), []);
  useEffect(() => { void load(); }, [load]);
  const review = async (id: string, status: "approved" | "rejected") => {
    try { await api(`/api/leaves/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }); await load(); }
    catch (error) { setNotice({ tone: "error", text: errorMessage(error) }); }
  };
  return (
    <main className="manager-main" id="main-content">
      <PageHeading title="การลา" description="พนักงานส่งคำขอและผู้จัดการอนุมัติในระบบ" />
      <NoticeBar notice={notice} onClose={() => setNotice(null)} />
      <section className="leave-list real-leave-list">{items.length === 0 ? <EmptyState icon={CalendarDays} title="ยังไม่มีคำขอลา" text="คำขอใหม่จะปรากฏที่หน้านี้" /> : items.map((item) => <article className={`leave-row status-${item.status}`} key={item.id}><span className="employee-avatar">{item.full_name.slice(0, 1)}</span><div className="leave-main"><strong>{item.full_name}</strong><span>{item.leave_type} · {item.start_date} ถึง {item.end_date}</span><p>{item.reason}</p></div><div className="leave-days"><strong>{item.days}</strong><span>วัน</span></div><div className="leave-actions">{item.status === "pending" ? <><button className="button primary" onClick={() => void review(item.id, "approved")}><Check size={16} /> อนุมัติ</button><button className="button secondary" onClick={() => void review(item.id, "rejected")}><X size={16} /> ไม่อนุมัติ</button></> : <span className={`leave-status ${item.status}`}>{item.status === "approved" ? "อนุมัติแล้ว" : "ไม่อนุมัติ"}</span>}</div></article>)}</section>
    </main>
  );
}

function PageHeading({ title, description, action }: { title: string; description: string; action?: React.ReactNode }) {
  return <section className="manager-heading"><div><h1>{title}</h1><p>{description}</p></div>{action}</section>;
}

function NoticeBar({ notice, onClose }: { notice: Notice; onClose: () => void }) {
  if (!notice) return null;
  return <div className={`manager-notice is-${notice.tone}`} role={notice.tone === "error" ? "alert" : "status"}><span>{notice.tone === "success" ? <Check size={18} /> : <AlertCircle size={18} />}</span><p>{notice.text}</p><button className="icon-button" onClick={onClose} aria-label="ปิดข้อความ"><X size={17} /></button></div>;
}

function EmptyState({ icon: Icon, title, text }: { icon: typeof UsersRound; title: string; text: string }) {
  return <div className="manager-empty-state"><Icon size={29} /><strong>{title}</strong><p>{text}</p></div>;
}

function errorMessage(error: unknown) { return error instanceof ApiError ? error.message : "ทำรายการไม่สำเร็จ กรุณาลองอีกครั้ง"; }
function csvCell(value: string | number) { const text = String(value); return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text; }
function downloadBlob(blob: Blob, filename: string) { const url = URL.createObjectURL(blob); const anchor = document.createElement("a"); anchor.href = url; anchor.download = filename; anchor.click(); window.setTimeout(() => URL.revokeObjectURL(url), 1000); }
